import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import PortfolioModal from "@/components/PortfolioModal";
import ProdPullModal from "@/components/ProdPullModal";
import WhatIsBotChainCP from "@/components/WhatIsBotChainCP";
import { MiningView, StakingView, CommunityView, BotMiningView } from "@/components/dashboard/streams";
import LiquidValueCard from "@/components/LiquidValueCard";
import StreamBreakdown from "@/components/dashboard/StreamBreakdown";
import WalletLedger from "@/components/dashboard/WalletLedger";
import ProfileModal from "@/components/ProfileModal";
import LevelProgress from "@/components/dashboard/LevelProgress";
import { usePaged, TablePager } from "@/components/dashboard/Pager";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useUiMode } from "@/contexts/UiModeContext";
import { todayIn } from "@/lib/dates";
import { CHART_START, chartEndDay, walletsBehind } from "@/lib/chartWindow";

/** "24 Sep" — a date he reads at a glance, in a sentence. */
const fmtShortDay = (d) => {
  if (!d) return "";
  const dt = new Date(`${String(d).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(dt.getTime()) ? d
    : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
};
import { estDailyYieldCa, stakingIncomeByDate } from "@/lib/staking";
import useCaPrice from "@/lib/useCaPrice";
import { toast } from "sonner";
import {
  Cpu, Coins, Users, Layers, Download, RefreshCw, Database, Plus, Pencil, ChevronRight, ChevronDown, AlertTriangle,
  Sparkles, Trophy, Calendar, Network, Table2, Copy, Wallet as WalletIcon, TrendingUp, DownloadCloud, Star, Server, Share2,
} from "lucide-react";
import {
  ComposedChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Brush, ReferenceLine, ReferenceDot,
} from "recharts";

const usd = (n, dec = 0) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: dec })}`;
const shortAddr = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a || "—");

const STREAMS = [
  { key: "mining", label: "CA Mining", color: "#8D1CEB", icon: Cpu },
  { key: "botmining", label: "BOT earnings", color: "#15DCAC", icon: Server },
  { key: "staking", label: "Staking", color: "#C2185B", icon: Coins },
  { key: "community", label: "Team", color: "#7C3AED", icon: Users },
];

// Stuart's dashboard time-series charts start no earlier than this date — trims
// the near-zero early-CaryPact-history lead-in. KPI totals still count everything.

function eachDay(start, end) {
  const out = []; const d = new Date(start + "T00:00:00Z"); const e = new Date(end + "T00:00:00Z");
  let guard = 0;
  while (d <= e && guard < 800) { out.push(d.toISOString().slice(0, 10)); d.setUTCDate(d.getUTCDate() + 1); guard++; }
  return out;
}

function DailyTooltip({ active, payload, label, showCommunity = true }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const mining = +(row.mining || 0);
  const staking = +(row.staking || 0);
  const community = +(row.community || 0);
  const bot = +(row.bot || 0);
  const total = mining + staking + community + bot;
  const rolling = row.rolling7;
  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div style={{ border: "1px solid rgba(15,18,38,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.97)", backdropFilter: "blur(20px)", padding: "10px 12px", minWidth: 210, fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: "#0F1226", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "3px 12px" }}>
        <div style={{ color: "#8D1CEB" }}>● CA Mining</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(mining)}</div>
        <div style={{ color: "#15DCAC" }}>● BOT Mining</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(bot)}</div>
        <div style={{ color: "#C2185B" }}>● Staking</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(staking)}</div>
        {showCommunity && <><div style={{ color: "#7C3AED" }}>● Community</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(community)}</div></>}
      </div>
      <div style={{ borderTop: "1px solid rgba(15,18,38,0.08)", marginTop: 8, paddingTop: 8, display: "grid", gridTemplateColumns: "1fr auto", gap: "3px 12px" }}>
        <div style={{ color: "#0F1226", fontWeight: 600 }}>Daily total</div>
        <div className="num" style={{ fontWeight: 700, textAlign: "right", color: "#0F1226" }}>{fmt(total)}</div>
        {rolling != null && (
          <>
            <div style={{ color: "#64748B" }}>7-day avg</div>
            <div className="num" style={{ fontWeight: 600, textAlign: "right", color: "#64748B" }}>{fmt(rolling)}</div>
          </>
        )}
        <div style={{ color: "#00863A", fontWeight: 600 }}>30-day pace</div>
        <div className="num" style={{ fontWeight: 700, textAlign: "right", color: "#00863A" }}>{fmt(total * 30)}</div>
      </div>
    </div>
  );
}

function PeriodTooltip({ active, payload, label, showCommunity = true }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const mining = +(row.mining || 0);
  const staking = +(row.staking || 0);
  const community = +(row.community || 0);
  const bot = +(row.bot || 0);
  const total = mining + staking + community + bot;
  const delta = row.delta;
  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div style={{ border: "1px solid rgba(15,18,38,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.97)", backdropFilter: "blur(20px)", padding: "10px 12px", minWidth: 196, fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: "#0F1226", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
        <Calendar size={12} /> {label}
        {row.isBest && <span style={{ marginLeft: "auto", color: "#B45309", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10 }}><Trophy size={10} /> Best</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "3px 12px" }}>
        <div style={{ color: "#8D1CEB" }}>● CA Mining</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(mining)}</div>
        <div style={{ color: "#15DCAC" }}>● BOT Mining</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(bot)}</div>
        <div style={{ color: "#C2185B" }}>● Staking</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(staking)}</div>
        {showCommunity && <><div style={{ color: "#7C3AED" }}>● Community</div><div className="num" style={{ fontWeight: 600, textAlign: "right" }}>{fmt(community)}</div></>}
      </div>
      <div style={{ borderTop: "1px solid rgba(15,18,38,0.08)", marginTop: 8, paddingTop: 8, display: "grid", gridTemplateColumns: "1fr auto", gap: "3px 12px" }}>
        <div style={{ color: "#0F1226", fontWeight: 600 }}>Period total</div>
        <div className="num" style={{ fontWeight: 700, textAlign: "right", color: "#0F1226" }}>{fmt(total)}</div>
        {delta != null && (
          <>
            <div style={{ color: delta >= 0 ? "#00863A" : "#B91C1C" }}>vs previous</div>
            <div className="num" style={{ fontWeight: 700, textAlign: "right", color: delta >= 0 ? "#00863A" : "#B91C1C" }}>
              {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PillToggle({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-lg" style={{ background: "rgba(15,18,38,0.05)" }} data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          data-testid={`${testid}-${o.value}`}
          aria-pressed={value === o.value}
          data-state={value === o.value ? "on" : "off"}
          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${value === o.value ? "bg-white text-ink shadow-sm" : "text-ink-3 hover:text-ink-2"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PeriodTick({ x, y, payload, rows = [], withTotal, dense }) {
  const row = rows.find((r) => r.label === payload?.value) || {};
  // Crowded axes get shorter text, never fewer bars: recharts would rather drop a
  // whole tick than let two collide, which left alternate bars with no total at all.
  const money = (v) => (dense && Math.abs(v) >= 1000
    ? `$${(v / 1000).toFixed(1)}k`
    : `$${Math.round(v).toLocaleString()}`);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={13} textAnchor="middle" fontSize={dense ? 9.5 : 11} fill="#6B7280">
        {dense ? String(payload?.value ?? "").replace(/^wk /, "") : payload?.value}
      </text>
      {withTotal && row.total != null && (
        <text x={0} y={dense ? 27 : 29} textAnchor="middle" fontSize={dense ? 10 : 11.5} fontWeight={700} fill="#0F1226">
          {money(row.total)}
        </text>
      )}
      {withTotal && row.delta != null && (
        <text x={0} y={dense ? 39 : 43} textAnchor="middle" fontSize={dense ? 9 : 10} fontWeight={600}
          fill={row.delta >= 0 ? "#00863A" : "#B91C1C"}>
          {`${row.delta >= 0 ? "\u25B2" : "\u25BC"} ${Math.abs(row.delta).toFixed(1)}%`}
        </text>
      )}
    </g>
  );
}

function DeltaLabel({ x, y, width, value }) {
  if (value == null) return null;
  const up = value >= 0;
  return (
    <text x={x + width / 2} y={y - 4} fill={up ? "#00863A" : "#B91C1C"} textAnchor="middle" fontSize={10} fontWeight={700}>
      {up ? "▲" : "▼"} {Math.abs(value).toFixed(0)}%
    </text>
  );
}

function StreamKPI({ label, value, sub, color, icon: Icon, tid, gradient, active, onClick, hint, onBreakdown, breakdownOpen }) {
  const accent = gradient ? "linear-gradient(90deg,#8D1CEB,#15DCAC,#C2185B,#7C3AED)" : (color || "#00863A");
  return (
    <button
      type="button"
      onClick={onClick}
      className="kpi-tile !p-4 fade-up text-left w-full h-full cursor-pointer relative overflow-hidden transition-transform duration-200 ease-out hover:-translate-y-1 hover:shadow-xl"
      style={{
        ...(gradient ? { background: "linear-gradient(135deg, rgba(141,28,235,0.08), rgba(21,220,172,0.08))" } : {}),
        ...(active
          ? { boxShadow: `0 0 0 2px ${color || "#00863A"}, 0 14px 30px -8px ${color || "#00863A"}55` }
          : {}),
      }}
      data-testid={tid}
      title={`Click to open the ${label} view`}
    >
      <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]" style={{ background: accent, opacity: active ? 1 : 0.65 }} />
      <div className="flex items-center justify-between mb-2">
        <div className="kpi-label flex items-center gap-1.5">
          {color && <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />}
          {label}
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color || "#00863A"}1A` }}>
            <Icon size={15} style={{ color: color || "#00863A" }} />
          </div>
        )}
      </div>
      <div className="kpi-num truncate" style={{ fontSize: 24, color: gradient ? "#00863A" : undefined }}>{value}</div>
      <div className="flex items-end justify-between gap-2 mt-1">
        {sub ? <div className="text-[11px] text-ink-3 min-w-0 truncate">{sub}</div> : <span />}
        <span className="inline-flex items-center gap-1.5 shrink-0">
          {onBreakdown && (
            <span
              role="button"
              tabIndex={0}
              data-testid={`${tid}-breakdown`}
              title="See every reward inside this tile"
              onClick={(e) => { e.stopPropagation(); onBreakdown(); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onBreakdown(); } }}
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md cursor-pointer transition-colors"
              style={{ background: `${color || "#00863A"}1A`, color: color || "#00863A" }}
            >
              {breakdownOpen ? "Hide" : "Details"} <ChevronDown size={10} />
            </span>
          )}
          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: color || "#00863A" }} data-testid={`${tid}-hint`}>
            {active ? "Showing" : (hint || "View")} <ChevronRight size={11} />
          </span>
        </span>
      </div>
    </button>
  );
}

export default function Dashboard() {
  const { user, activeWalletId } = useAuth();
  const { showCommunity } = useUiMode();
  const isAdmin = user?.role === "admin";
  const isDemo = !!user?.is_demo;
  const [params, setParams] = useSearchParams();
  const [portfolios, setPortfolios] = useState([]);
  const [sel, setSel] = useState(() => localStorage.getItem("portfolio_sel") || "downline");
  const [modal, setModal] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [prodPullOpen, setProdPullOpen] = useState(false);
  const [hold, setHold] = useState(null); // portfolio holdings payload
  const [ledger, setLedger] = useState(null); // {mining, community, positions, wallets}
  const [error, setError] = useState("");
  const [notLinked, setNotLinked] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [backupBusy, setBackupBusy] = useState(false);
  const [rangeKey, setRangeKey] = useState("all");
  const [chartMode, setChartMode] = useState("daily");
  const [barGrain, setBarGrain] = useState("weekly");
  // Default to one total bar — category bars made a rising business look flat.
  const [barMode, setBarMode] = useState("total");
  const [me, setMe] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const { price: livePrice, botPrice } = useCaPrice();

  const viewKeys = ["mining", "botmining", "staking", ...(showCommunity ? ["community"] : [])];
  const view = viewKeys.includes(params.get("view")) ? params.get("view") : "all";
  const setView = (k) => setParams(k === "all" ? {} : { view: k }, { replace: true });

  const pick = (id) => { setSel(id); try { localStorage.setItem("portfolio_sel", id); } catch { /* private mode */ } };

  // Non-admins get the built-in views only; stale named ids fall back to Leader.
  // The demo is pinned to the showcase portfolio — the backend hands it nothing else.
  const showcaseId = portfolios.find((p) => p.leader_showcase)?.id;
  const effSel = isDemo
    ? (showcaseId || "downline")
    : ((isAdmin || sel === "wallet" || sel === "community") ? sel : "downline");

  const loadPortfolios = useCallback(async () => {
    try { const r = await api.get("/portfolios"); setPortfolios(r.data); } catch { /* non-admin */ }
  }, []);

  useEffect(() => { if (isAdmin) loadPortfolios(); }, [isAdmin, loadPortfolios]);

  // Who's looking: their rank, how long they've been running, phone on file.
  useEffect(() => {
    api.get("/me/summary").then(({ data }) => setMe(data)).catch(() => setMe(null));
  }, [user?.id, profileOpen]);

  // Holdings (tree sync, portfolio-scopable)
  useEffect(() => {
    setHold(null); setNotLinked(false);
    api.get(`/portfolios/${effSel}/revenue`)
      .then((r) => setHold(r.data))
      .catch((err) => {
        if (err.response?.status === 403) setNotLinked(true);
        else if (err.response?.status === 404 && effSel !== "downline") { pick("downline"); }
        else setError(formatApiError(err.response?.data?.detail));
      });
  }, [effSel, refreshKey]);

  // Revenue ledger (CaryPact-imported entries, scoped to the selected portfolio)
  useEffect(() => {
    setError("");
    (async () => {
      try {
        const pf = { params: { portfolio: effSel } };
        const [m, c, s, w, se, bs] = await Promise.all([
          api.get("/mining-entries", pf), api.get("/community-revenue", pf), api.get("/staking-positions", pf), api.get("/wallets"),
          api.get("/staking-entries", pf).catch(() => ({ data: [] })),
          api.get("/bot-staking", pf).catch(() => ({ data: null })),
        ]);
        setLedger({ mining: m.data, community: c.data, positions: s.data, wallets: w.data, stakingEntries: se.data, botStake: bs.data });
      } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    })();
  }, [refreshKey, effSel]);

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      await api.post("/admin/backup");
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `botchaincp_backup_${Date.now()}.tar.gz`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBackupBusy(false); }
  };

  // Synced-only: the Dashboard counts imported CaryPact records exclusively (user decision 2026-08-15).
  const mining = useMemo(() => (ledger?.mining || []).filter((e) => e.imported), [ledger]);
  const communityData = useMemo(() => (showCommunity ? (ledger?.community || []).filter((e) => e.imported) : []), [ledger, showCommunity]);
  const positions = useMemo(() => (ledger?.positions || []).filter((p) => p.imported), [ledger]);
  const stakingEntries = useMemo(() => (ledger?.stakingEntries || []).filter((e) => e.imported), [ledger]);
  // BOT staking (CaryPact's "BOT pledge") is the same product in a different asset,
  // so it is ADDED to the staking total and plotted on the same staking line — no
  // second line, no second tile.
  const botStake = ledger?.botStake || null;
  const botStakeEntries = useMemo(() => (botStake?.entries || []).filter((e) => e.imported), [botStake]);

  const caPrice = useMemo(() => {
    if (livePrice) return Number(livePrice);
    if (hold?.ca_price) return Number(hold.ca_price);
    const priced = [...mining, ...communityData].filter((e) => e.ca_price).sort((a, b) => a.date.localeCompare(b.date));
    return priced.length ? priced[priced.length - 1].ca_price : 28;
  }, [livePrice, hold, mining, communityData]);

  const totalMiningUsd = mining.reduce((a, e) => a + (e.usd_value || 0), 0);
  const totalCommunityUsd = communityData.reduce((a, e) => a + (e.usd_value || 0), 0);
  // Real staking rewards = sum of every position's compounded rewards (CA)
  // × current CA price. For CaryPact-synced positions this is the platform's
  // totalReward field (source of truth). For manual positions it's computed
  // via `_compute_staking` (compound interest formula, honouring auto_compound
  // and principal drip). We show the CA total in the tile so it's transparent
  // where the USD number comes from.
  const totalStakingCa = positions.reduce((a, p) => a + (p.earned_rewards || 0), 0);
  const botStakeBot = botStake?.totals?.earned_bot || 0;
  const botStakeUsd = botStake?.totals?.earned_usd || 0;
  const totalStakingUsd = totalStakingCa * caPrice + botStakeUsd;
  // BOT total spans BOT_A (mining) + BOT_B (hashrate mining), both paid in BOT.
  // bot_hash_* is the VIP level dividend, already inside bot_reward_* — never add it on top.
  const botTotalBot = hold?.totals?.bot_reward_total || 0;
  const botAvailBot = hold?.totals?.bot_reward || 0;
  const totalBotUsd = botTotalBot * (botPrice || 0);
  // The three referral / sales screens live inside their parent tile, never on
  // their own: VIP hashrate referral under CA Comm Rev, server sales + BOT power
  // referral under BOT Comm. All three settle in USDT, so their USD is 1:1.
  const rs = hold?.totals?.reward_streams || {};
  const vipReferralUsd = rs.vip_hashrate_referral?.usd || 0;
  const serverSalesUsd = rs.server_sales?.usd || 0;
  const botPowerReferralUsd = rs.bot_power_referral?.usd || 0;
  // BOT Hashrate Mining: hashrate bought with USDT paying BOT daily, on its own
  // CaryPact screens — separate money from static mining and the level dividend.
  const botPowerMiningUsd = rs.bot_power_mining?.usd || 0;
  // BOT Staking Level Income — CaryPact's new Community Revenue screen: BOT paid on
  // your V-level against the BOT staking beneath you. Its own money, so it's added.
  const botStakeLevelUsd = rs.bot_stake_level?.usd || 0;
  const caCommFamilyUsd = totalCommunityUsd + vipReferralUsd;
  const botFamilyUsd = totalBotUsd + serverSalesUsd + botPowerReferralUsd + botPowerMiningUsd + botStakeLevelUsd;
  const grandTotal = totalMiningUsd + botFamilyUsd + caCommFamilyUsd + totalStakingUsd;
  const totals = { mining: totalMiningUsd, botmining: botFamilyUsd, staking: totalStakingUsd, community: caCommFamilyUsd };

  // The referral / sales screens are dated money too, so they belong on the charts
  // and not only in the tiles: server sales + BOT power referral ride inside BOT,
  // VIP hashrate referral inside CA community. Only days CaryPact actually itemised
  // get a value — an undated running total stays in the tile and is disclosed there.
  const rewardByDate = useMemo(() => {
    const bot = {}, comm = {};
    (hold?.reward_series || []).forEach((r) => {
      bot[r.date] = (bot[r.date] || 0) + (r.server_sales_usd || 0) + (r.bot_power_referral_usd || 0) + (r.bot_power_mining_usd || 0) + (r.bot_stake_level_usd || 0);
      comm[r.date] = (comm[r.date] || 0) + (r.vip_referral_usd || 0);
    });
    return { bot, comm };
  }, [hold]);

  // The newest day each wallet in this selection has REPORTED money for, and the
  // newest day anything reported. One quiet wallet used to cut the whole chart
  // short — four healthy wallets hidden behind one with an expired session — so the
  // range now follows the newest day, and this says which wallet fell behind.
  const reportedDays = useMemo(() => {
    const byWallet = {};
    const note = (wid, date) => {
      if (!wid || !date) return;
      byWallet[wid] = byWallet[wid] > date ? byWallet[wid] : date;
    };
    mining.forEach((e) => note(e.wallet_id, e.date));
    communityData.forEach((e) => note(e.wallet_id, e.date));
    stakingEntries.forEach((e) => note(e.wallet_id, e.date));
    botStakeEntries.forEach((e) => note(e.wallet_id, e.date));
    const all = Object.values(byWallet);
    return { byWallet, newest: all.length ? all.reduce((a, b) => (a > b ? a : b)) : null };
  }, [mining, communityData, stakingEntries, botStakeEntries]);

  const behindWallets = useMemo(() => {
    const newest = reportedDays.newest;
    if (!newest) return [];
    const names = {};
    (ledger?.wallets || []).forEach((w) => { names[w.id] = w.label || w.name || w.wallet_address; });
    return walletsBehind(reportedDays.byWallet, newest).map((w) => ({
      ...w,
      name: names[w.id] || "A wallet",
      address: (ledger?.wallets || []).find((x) => x.id === w.id)?.wallet_address,
    }));
  }, [reportedDays, ledger]);

  // Daily combined series from the real ledger (mining + community actual, staking estimated)
  const series = useMemo(() => {
    const dates = [
      ...mining.map((e) => e.date),
      ...communityData.map((e) => e.date),
      ...positions.map((p) => p.date_staked),
    ].filter(Boolean).sort();
    if (!dates.length) return [];
    let start = dates[0];
    // End at the NEWEST day anything in this selection actually reported. It used to
    // end at the EARLIEST of the streams to dodge a one-day lag, which meant a single
    // wallet whose session expired on 24 Sep hid every other wallet's October.
    // Quiet contributors simply add nothing after their last day, so the running
    // total holds flat instead of dipping — money already earned never vanishes.
    let end = chartEndDay(reportedDays.newest, todayIn());
    if (start > end) return [];
    const maxStart = new Date(end + "T00:00:00Z"); maxStart.setUTCDate(maxStart.getUTCDate() - 370);
    if (new Date(start + "T00:00:00Z") < maxStart) start = maxStart.toISOString().slice(0, 10);
    // Charts open on 20 May 2026 for everyone — the earlier import days are
    // partial. KPI totals above are computed separately and still count them.
    if (start < CHART_START) start = CHART_START;
    if (start > end) return [];
    const mByDate = {}; mining.forEach((e) => { mByDate[e.date] = (mByDate[e.date] || 0) + (e.usd_value || 0); });
    const cByDate = {}; communityData.forEach((e) => { cByDate[e.date] = (cByDate[e.date] || 0) + (e.usd_value || 0); });
    const bByDate = {}; (hold?.series || []).forEach((e) => { bByDate[e.date] = (bByDate[e.date] || 0) + (e.bot_usd || 0); });
    // Staking is EARNED money now (CaryPact reward records when imported, else each
    // order's real lifetime rewards spread over the days it was live) — never a forecast.
    const { byDate: sByDate } = stakingIncomeByDate(stakingEntries, positions, caPrice, todayIn());
    // BOT staking rewards join the SAME staking line, in USD at the BOT price of
    // the day each was credited.
    botStakeEntries.forEach((e) => { sByDate[e.date] = (sByDate[e.date] || 0) + (e.usd_value || 0); });
    return eachDay(start, end).map((d) => ({
      date: d,
      mining: +(mByDate[d] || 0).toFixed(2),
      community: +((cByDate[d] || 0) + (rewardByDate.comm[d] || 0)).toFixed(2),
      staking: +(sByDate[d] || 0).toFixed(2),
      bot: +((bByDate[d] || 0) + (rewardByDate.bot[d] || 0)).toFixed(2),
    }));
  }, [mining, communityData, positions, stakingEntries, botStakeEntries, caPrice, hold, rewardByDate, reportedDays]);

  const seriesView = useMemo(() => {
    if (!series.length) return [];
    const days = { "7d": 7, "30d": 30, "90d": 90 }[rangeKey];
    const sliced = days ? series.slice(-days) : series;
    let cM = 0, cS = 0, cC = 0, cB = 0;
    return sliced.map((d, i, arr) => {
      const start = Math.max(0, i - 6);
      const win = arr.slice(start, i + 1);
      const rolling7 = win.reduce((a, x) => a + (x.mining + x.staking + x.community + (x.bot || 0)), 0) / win.length;
      cM += d.mining; cS += d.staking; cC += d.community; cB += (d.bot || 0);
      const dailyTotal = d.mining + d.staking + d.community + (d.bot || 0);
      if (chartMode === "cumulative") {
        return { date: d.date, mining: +cM.toFixed(2), staking: +cS.toFixed(2), community: +cC.toFixed(2), bot: +cB.toFixed(2), rolling7: +rolling7.toFixed(2), dailyTotal: +dailyTotal.toFixed(2), allStreams: +(cM + cS + cC + cB).toFixed(2) };
      }
      return { ...d, rolling7: +rolling7.toFixed(2), dailyTotal: +dailyTotal.toFixed(2), allStreams: +dailyTotal.toFixed(2) };
    });
  }, [series, rangeKey, chartMode]);

  const peakDay = useMemo(() => {
    if (!seriesView.length) return null;
    return seriesView.reduce((m, d) => (d.dailyTotal > (m?.dailyTotal || -1) ? d : m), null);
  }, [seriesView]);

  const dailyAvg = useMemo(() => {
    if (chartMode === "cumulative" || !seriesView.length) return null;
    const sum = seriesView.reduce((a, d) => a + d.dailyTotal, 0);
    return +(sum / seriesView.length).toFixed(2);
  }, [seriesView, chartMode]);

  const barBuckets = useMemo(() => {
    if (!series.length) return [];
    if (barGrain === "monthly") {
      const map = new Map();
      series.forEach(({ date, mining: m, staking: s, community: c, bot: b }) => {
        const k = date.slice(0, 7);
        if (!map.has(k)) map.set(k, { label: k, mining: 0, staking: 0, community: 0, bot: 0 });
        const row = map.get(k);
        row.mining += m; row.staking += s; row.community += c; row.bot += (b || 0);
      });
      return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }
    if (barGrain === "weekly") {
      const map = new Map();
      series.forEach(({ date, mining: m, staking: s, community: c, bot: b }) => {
        const d = new Date(date + "T00:00:00Z");
        const day = (d.getUTCDay() + 6) % 7;
        const monday = new Date(d); monday.setUTCDate(d.getUTCDate() - day);
        const k = monday.toISOString().slice(0, 10);
        if (!map.has(k)) map.set(k, { label: `wk ${k.slice(5)}`, sortKey: k, mining: 0, staking: 0, community: 0, bot: 0 });
        const row = map.get(k);
        row.mining += m; row.staking += s; row.community += c; row.bot += (b || 0);
      });
      return Array.from(map.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }
    return series.map((d) => ({ label: d.date.slice(5), mining: d.mining, staking: d.staking, community: d.community, bot: d.bot || 0 }));
  }, [series, barGrain]);

  const barView = useMemo(() => {
    if (!barBuckets.length) return [];
    const withTotals = barBuckets.map((m, i, arr) => {
      const total = m.mining + m.staking + m.community + (m.bot || 0);
      const prev = i > 0 ? (arr[i - 1].mining + arr[i - 1].staking + arr[i - 1].community + (arr[i - 1].bot || 0)) : null;
      const delta = prev != null && prev > 0 ? ((total - prev) / prev) * 100 : null;
      return { ...m, total: +total.toFixed(2), delta };
    });
    const maxTotal = Math.max(...withTotals.map((m) => m.total));
    return withTotals.map((m) => ({ ...m, isBest: m.total === maxTotal && maxTotal > 0 }));
  }, [barBuckets]);

  const barAvg = useMemo(() => {
    if (!barView.length) return null;
    return +(barView.reduce((a, m) => a + m.total, 0) / barView.length).toFixed(2);
  }, [barView]);

  // Every bar keeps its total; the text shrinks instead of the tick count dropping.
  // Roughly 70px per bar is what "$10,476" plus a delta needs to breathe.
  const barDense = barView.length > Math.max(8, Math.floor((window.innerWidth || 1280) / 70));

  const t = hold?.totals || {};

  const lastSync = useMemo(() => {
    const ds = (hold?.wallets || []).map((w) => w.cp_synced_at).filter(Boolean).sort();
    return ds.length ? ds[ds.length - 1].slice(0, 10) : null;
  }, [hold]);

  const activeStats = useMemo(() => {
    const list = hold?.wallets || [];
    const active = list.filter((w) => (w.mining_usdt || 0) > 0 || (w.staked_ca || 0) > 0 || (w.power || 0) > 0 || (w.miner_usd || 0) > 0).length;
    return { active, unused: list.length - active };
  }, [hold]);
  const [topSort, setTopSort] = useState({ key: "rank", dir: "desc" });
  const topWallets = useMemo(() => {
    const rankNum = (r) => { const m = /^V(\d+)$/i.exec(String(r || "").trim()); return m ? +m[1] : -1; };
    const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });
    const s = topSort.dir === "asc" ? 1 : -1;
    // A missing value is "unknown", not "lowest". Blanks stay at the BOTTOM whichever
    // way the column is pointing, so flipping V5→V1 round to V1→V5 can't fill the top
    // of the table with wallets that have no rank at all.
    const blank = (v) => v == null || String(v).trim() === "";
    const blanksLast = (a, b) => (blank(a) ? 1 : 0) - (blank(b) ? 1 : 0);
    const cmp = (a, b) => {
      switch (topSort.key) {
        case "name":    return blanksLast(a.name, b.name) || cmpStr(a.name, b.name) * s;
        case "address": return blanksLast(a.address, b.address) || cmpStr(a.address, b.address) * s;
        case "rank": {
          const ra = rankNum(a.rank), rb = rankNum(b.rank);
          if (ra < 0 || rb < 0) return (ra < 0 ? 1 : 0) - (rb < 0 ? 1 : 0);
          return (ra - rb) * s;
        }
        case "mining":  return ((a.mining_usdt || 0) - (b.mining_usdt || 0)) * s;
        case "downline": return ((a.team_lock_usd || 0) - (b.team_lock_usd || 0)) * s;
        case "staked":  return ((a.staked_usd || 0) - (b.staked_usd || 0)) * s;
        default:        return 0;
      }
    };
    return [...(hold?.wallets || [])]
      .sort((a, b) => cmp(a, b) || (b.mining_usdt || 0) - (a.mining_usdt || 0));
  }, [hold, topSort]);
  const topPager = usePaged(topWallets, 12);
  const dailyRows = useMemo(() => [...series].reverse(), [series]);
  const dailyPager = usePaged(dailyRows, 12);
  const toggleTopSort = (key) => setTopSort((p) => ({ key, dir: p.key === key && p.dir === "desc" ? "asc" : "desc" }));
  const sortArrow = (key) => (topSort.key === key ? (topSort.dir === "desc" ? " ▼" : " ▲") : "");

  const copyAddr = async (a) => {
    try { await navigator.clipboard.writeText(a); toast.success("Address copied"); } catch { /* blocked */ }
  };

  const downloadCsv = async () => {
    // The three referral / sales streams keep their own dated records, so the export
    // carries them as their own columns — the totals row then matches the tiles.
    let extra = {};
    try {
      const { data } = await api.get(`/portfolios/${effSel}/reward-streams`);
      (data?.parents || []).forEach((p) => (p.components || []).forEach((c) => {
        if (!c.key.startsWith("ca_community_reward") && !c.key.startsWith("bot_level")) {
          extra[c.key] = { label: c.label, byDate: Object.fromEntries((c.series || []).map((s) => [s.date, s.usd])) };
        }
      }));
    } catch { /* export the core streams anyway */ }
    const keys = Object.keys(extra);
    const rows = [["Date", "Mining USD", "Staking USD", ...(showCommunity ? ["Community USD"] : []), "BOT USD", ...keys.map((k) => `${extra[k].label} USD`), "Total USD"]];
    series.forEach((r) => {
      const ex = keys.map((k) => +(extra[k].byDate[r.date] || 0).toFixed(2));
      rows.push([r.date, r.mining, r.staking, ...(showCommunity ? [r.community] : []), r.bot || 0, ...ex,
        +(r.mining + r.staking + r.community + (r.bot || 0) + ex.reduce((a, b) => a + b, 0)).toFixed(2)]);
    });
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `dashboard-revenue-${todayIn()}.csv`;
    a.click();
  };

  const ledgerEmpty = ledger && grandTotal === 0 && series.length === 0;

  if (notLinked && ledgerEmpty) {
    return (
      <div className="min-h-screen pb-16">
        <Header />
        <div className="max-w-[900px] mx-auto px-6 pt-16 text-center glass-strong p-10 mt-10" data-testid="dashboard-not-linked">
          <Network size={28} className="mx-auto mb-3 text-ink-3" />
          <div className="font-display text-xl font-semibold">You're not linked to a team yet.</div>
          <p className="text-sm text-ink-2 mt-2">Once your admin links your account to a wallet on the org chart, your dashboard lights up automatically with synced revenue.</p>
        </div>
        <HelpFab />
      </div>
    );
  }

  const loading = !ledger;

  return (
    <div className="min-h-screen pb-16" data-testid="dashboard-page">
      <Header />
      <div className="max-w-[1850px] mx-auto px-6 md:px-10 pt-3" style={{ zoom: 0.75 }}>

        {/* Page header — the greeting owns the top line now (it used to sit under
            the sync pill, leaving a band of dead space above it). */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5 mb-3 fade-up">
          <div className="shrink-0">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-none" data-testid="dash-greeting">
              Hello, {me?.first_name || (user?.name || "").split(" ")[0] || "there"}.
            </h1>
            <div className="mt-2.5 flex items-baseline gap-x-3 gap-y-1 flex-wrap font-display font-bold text-xl sm:text-2xl leading-none" data-testid="dash-greeting-sub">
              {me?.rank && (
                <span data-testid="dash-greeting-rank" style={{ color: "#8D1CEB" }}>Rank {me.rank}</span>
              )}
              {me?.days_active != null && (
                <>
                  {me?.rank && <span className="text-base font-normal" style={{ color: "rgba(15,18,38,0.25)" }}>·</span>}
                  <span data-testid="dash-greeting-days">
                    <span style={{ color: "#0FB999" }}>{me.days_active}</span> <span className="text-ink">days active</span>
                  </span>
                  {me.started_on && (
                    <span className="text-[13px] font-sans font-medium text-ink-3">since {me.started_on}</span>
                  )}
                </>
              )}
            </div>
          </div>
          <LevelProgress level={me?.level} />
          <div className="flex flex-col items-start lg:items-end gap-2 shrink-0">
            <div className="pill"><span className="dot" />Auto-synced from CaryPact · no manual entry</div>
            <div className="flex items-center gap-2 flex-wrap md:justify-end">
            <button onClick={() => setRefreshKey((k) => k + 1)} className="btn-secondary" data-testid="dash-refresh-btn"><RefreshCw size={14} /> Refresh</button>
            <button onClick={downloadCsv} disabled={!series.length} className="btn-secondary" data-testid="dash-download-csv"><Download size={14} /> CSV</button>
            {isAdmin && !user?.is_demo && (
              <button onClick={downloadBackup} disabled={backupBusy} className="btn-secondary" data-testid="dash-backup-btn" title="Create a fresh snapshot and download it. Daily auto-backups also run at 03:00 UTC.">
                <Database size={14} /> {backupBusy ? "Backing up…" : "Backup"}
              </button>
            )}
            {user?.is_super_admin && !user?.is_demo && window.location.hostname !== "botchaincp.com" && (
              <button onClick={() => setProdPullOpen(true)} className="btn-secondary" data-testid="prod-pull-btn" title="Preview only: replace ALL preview data with an exact clone of production (production is read-only, never changed).">
                <DownloadCloud size={14} /> Pull from production
              </button>
            )}
            </div>
          </div>
        </div>

        {/* No phone on file — the one thing we can't recover an account without. */}
        {me?.phone_missing && (
          <div className="glass p-3.5 mb-3 flex items-center gap-3 flex-wrap fade-up"
            style={{ border: "1.5px solid rgba(245,158,11,0.45)", background: "rgba(245,158,11,0.07)" }}
            data-testid="dash-phone-banner">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(245,158,11,0.16)" }}>
              <AlertTriangle size={16} style={{ color: "#B45309" }} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink">Add your phone number</div>
              <div className="text-[12px] text-ink-2">
                There's no way to reach you about your wallet. Accounts with no phone number may be deactivated
                {me.phone_days_left != null ? ` in ${me.phone_days_left} day${me.phone_days_left === 1 ? "" : "s"}` : ` after ${me.grace_days} days`}.
              </div>
            </div>
            <button onClick={() => setProfileOpen(true)} className="btn-primary ml-auto shrink-0" data-testid="dash-phone-cta">
              Add my phone
            </button>
          </div>
        )}

        {/* Portfolio picker — drives EVERY number on this page (tiles, charts, tables) */}
        {isDemo ? (
          <div className="flex flex-wrap items-center gap-2.5 mb-3 fade-up" data-testid="dash-demo-scope-bar">
            <span className="px-5 py-2.5 rounded-2xl text-[15px] font-bold inline-flex items-center gap-2"
              style={{ background: "white", color: "#0F1226", boxShadow: "0 6px 20px rgba(64,96,255,0.25)", border: "1.5px solid rgba(64,96,255,0.45)" }}>
              <Network size={16} style={{ color: "#4060FF" }} /> {hold?.portfolio?.name || "Leader showcase"}
            </span>
          </div>
        ) : !notLinked && (
          <div className="flex flex-wrap items-center gap-2.5 mb-3 fade-up" data-testid="dash-portfolio-bar">
            <button
              onClick={() => pick("wallet")}
              className="px-5 py-2.5 rounded-2xl text-[15px] font-bold inline-flex items-center gap-2 transition-all"
              style={effSel === "wallet"
                ? { background: "white", color: "#0F1226", boxShadow: "0 6px 20px rgba(64,96,255,0.25)", border: "1.5px solid rgba(64,96,255,0.45)" }
                : { background: "rgba(15,18,38,0.05)", color: "#5A607F", border: "1.5px solid transparent" }}
              title={activeWalletId ? "Just this wallet — no downline" : "Every wallet linked to your account, aggregated"}
              data-testid="portfolio-chip-wallet"
            >
              <WalletIcon size={16} style={effSel === "wallet" ? { color: "#4060FF" } : undefined} /> {activeWalletId ? "This wallet" : "This Account"}
            </button>
            <button
              onClick={() => pick("downline")}
              className="px-5 py-2.5 rounded-2xl text-[15px] font-bold inline-flex items-center gap-2 transition-all"
              style={effSel === "downline"
                ? { background: "white", color: "#0F1226", boxShadow: "0 6px 20px rgba(64,96,255,0.25)", border: "1.5px solid rgba(64,96,255,0.45)" }
                : { background: "rgba(15,18,38,0.05)", color: "#5A607F", border: "1.5px solid transparent" }}
              title="Everything under this account — all wallets and their downlines"
              data-testid="portfolio-chip-downline"
            >
              <Network size={16} style={effSel === "downline" ? { color: "#4060FF" } : undefined} /> Leader portfolio
            </button>
            <button
              onClick={() => pick("community")}
              className="px-5 py-2.5 rounded-2xl text-[15px] font-bold inline-flex items-center gap-2 transition-all"
              style={effSel === "community"
                ? { background: "white", color: "#0F1226", boxShadow: "0 6px 20px rgba(64,96,255,0.25)", border: "1.5px solid rgba(64,96,255,0.45)" }
                : { background: "rgba(15,18,38,0.05)", color: "#5A607F", border: "1.5px solid transparent" }}
              title="The selected wallet + every wallet below it"
              data-testid="portfolio-chip-community"
            >
              <Users size={16} style={effSel === "community" ? { color: "#7C3AED" } : undefined} /> My Community
            </button>
            {isAdmin && portfolios.map((p) => (
              <div key={p.id} className="inline-flex items-center rounded-2xl overflow-hidden transition-all"
                style={effSel === p.id
                  ? { background: "white", boxShadow: "0 6px 20px rgba(64,96,255,0.25)", border: "1.5px solid rgba(64,96,255,0.45)" }
                  : { background: "rgba(15,18,38,0.05)", border: "1.5px solid transparent" }}>
                <button onClick={() => pick(p.id)} className="pl-5 pr-2 py-2.5 text-[15px] font-bold inline-flex items-center gap-2" style={{ color: effSel === p.id ? "#0F1226" : "#5A607F" }} data-testid={`portfolio-chip-${p.id}`}>
                  <Layers size={16} style={effSel === p.id ? { color: "#7C3AED" } : undefined} />
                  {p.name} <span className="text-[12px] font-mono text-ink-3">({(p.wallet_addresses || []).length})</span>
                  {p.leader_showcase && <Star size={13} style={{ color: "#B45309", fill: "#F59E0B" }} title="Shown to everyone as the Leader portfolio" data-testid={`portfolio-showcase-star-${p.id}`} />}
                </button>
                <button onClick={() => setModal(p)} className="pr-4 pl-1.5 py-2.5 text-ink-3 hover:text-ink" title={`Edit ${p.name}`} data-testid={`portfolio-edit-${p.id}`}><Pencil size={14} /></button>
              </div>
            ))}
            {isAdmin && (
              <button onClick={() => setModal({})} className="px-4 py-2.5 rounded-2xl text-[15px] font-bold inline-flex items-center gap-2 transition-colors hover:bg-white/70" style={{ border: "1.5px dashed rgba(64,96,255,0.45)", color: "#4060FF" }} data-testid="dash-new-portfolio-btn">
                <Plus size={16} /> New portfolio
              </button>
            )}
            {hold?.portfolio && (
              <span className="ml-auto text-[12px] font-mono text-ink-3" data-testid="dash-portfolio-scope-note">
                Showing <b className="text-ink-2">{hold.portfolio.name}</b> · {activeStats.active} active wallet{activeStats.active === 1 ? "" : "s"}
              </span>
            )}
          </div>
        )}

        {/* Selection summary — one line, simple math, changes with every pick */}
        {!notLinked && hold && (
          <div className="glass p-3 mb-3 flex flex-wrap items-center gap-x-7 gap-y-1.5 fade-up" data-testid="dash-selection-stats">
            <span className="inline-flex items-center gap-2" data-testid="dash-stat-wallets"
              title={activeStats.unused > 0 ? `${activeStats.unused} dormant wallet${activeStats.unused === 1 ? "" : "s"} (no mining, staking, power or rigs)` : undefined}>
              <WalletIcon size={15} style={{ color: "#4060FF" }} />
              {(activeStats.active + activeStats.unused) > 1 ? (
                <>
                  <span className="text-sm text-ink-2">Total wallets</span>
                  <b className="font-display text-xl font-bold">{Number(activeStats.active + activeStats.unused).toLocaleString()}</b>
                  <span className="text-[11px] font-mono text-ink-3">· active {activeStats.active}, dormant {activeStats.unused}</span>
                </>
              ) : (
                <>
                  <b className="font-display text-xl font-bold">{Number(activeStats.active || 0).toLocaleString()}</b>
                  <span className="text-sm text-ink-2">active wallet{(activeStats.active || 0) === 1 ? "" : "s"}</span>
                </>
              )}
            </span>
            <span className="inline-flex items-baseline gap-2 flex-wrap" data-testid="dash-stat-value"
              title={hold.team ? `CaryPact effective performance: ${usd(hold.team.effective_usd)} · team rig volume: ${usd(hold.team.miner_usd)}` : undefined}>
              <span className="text-sm text-ink-2">{(t.wallets || 0) > 1 ? "Team value" : "Wallet value"}</span>
              <b className="font-display text-xl font-bold">{usd((t.mining_locked_usd || 0) + (t.staked_usd || 0) + (t.rig_usd || 0))}</b>
              <span className="text-[12px] font-mono text-ink-3">= {usd(t.mining_locked_usd)} mining + {usd(t.staked_usd)} staking + {usd(t.rig_usd)} rigs</span>
            </span>
            {hold.team && hold.team.effective_usd > 0 && (
              <span className="inline-flex items-baseline gap-2 flex-wrap" data-testid="dash-stat-community"
                title={`Straight from CaryPact on your root wallet — the same four numbers as My Community. Total Community Effective Performance (teamLockUsdtAmount) ${usd(hold.team.effective_usd)} = Community Hashrate Deposit Performance (teamTotalLockUsdtAmount) ${usd(hold.team.hashrate_usd)} + Community Miner Performance (teamMinerPerformanceAmount) ${usd(hold.team.miner_usd)} + Total Community Staking Performance ${usd(hold.team.staking_usd)}. It covers your whole team, so it doesn't change with the portfolio you pick.${hold.team.synthesized ? " Rolled up from the tiles below you — CaryPact hasn't reported it directly for this wallet." : ""}`}>
                <span className="text-sm text-ink-2">Community effective</span>
                <b className="font-display text-xl font-bold">{usd(hold.team.effective_usd)}</b>
                <span className="text-[12px] font-mono text-ink-3">
                  = {usd(hold.team.hashrate_usd)} hashrate deposit + {usd(hold.team.miner_usd)} miner + {usd(hold.team.staking_usd)} staking
                </span>
                {hold.team.synthesized && <span className="text-[11px] font-mono" style={{ color: "#B45309" }}>rolled up</span>}
              </span>
            )}
            {lastSync && <span className="ml-auto text-[12px] font-mono text-ink-3">Synced {lastSync}</span>}
          </div>
        )}

        {error && <div className="chip chip-amber mb-4" data-testid="dashboard-error">{error}</div>}

        {loading ? (
          <div className="text-sm text-ink-3 font-mono py-16 text-center" data-testid="dashboard-loading">Loading dashboard…</div>
        ) : (
          <>
            {/* Stream tabs — quick shareable views */}
            <div className="inline-flex flex-wrap gap-1 p-1 rounded-2xl glass mb-3" data-testid="dash-view-switcher" style={{ borderRadius: 16 }}>
              {[{ key: "all", label: "Overall", icon: Layers }, ...STREAMS.filter((s) => showCommunity || s.key !== "community")].map((v) => {
                const Icon = v.icon;
                const active = view === v.key;
                return (
                  <button
                    key={v.key}
                    onClick={() => setView(v.key)}
                    data-testid={`dash-tab-${v.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${active ? "bg-white text-ink shadow-sm" : "text-ink-2 hover:bg-white/50"}`}
                    style={active ? { boxShadow: "0 4px 14px rgba(64,96,255,0.18)" } : undefined}
                  >
                    <Icon size={15} style={active ? { color: v.color || "#4060FF" } : undefined} />
                    {v.label}
                  </button>
                );
              })}
            </div>

            {/* Income tiles — horizontal strip on top · selected view full-width below */}
            <div className="mb-3">
              <div className={`grid gap-3 mb-3 ${showCommunity ? "grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" : "grid-cols-2 lg:grid-cols-4"}`} data-testid="dash-kpi-column">
                <StreamKPI label="CA Mining" value={usd(totalMiningUsd)} sub="CaryPact mining rewards received" hint="View" color="#8D1CEB" icon={Cpu} active={view === "mining"} onClick={() => setView(view === "mining" ? "all" : "mining")} tid="dash-kpi-mining" />
                <StreamKPI
                  label="BOT earnings"
                  value={botPrice ? usd(botFamilyUsd, 2) : `${Number(botTotalBot).toLocaleString(undefined, { maximumFractionDigits: 4 })} BOT`}
                  sub={botPrice
                    ? `${Number(botTotalBot).toLocaleString(undefined, { maximumFractionDigits: 4 })} BOT mining + dividend${botPowerMiningUsd > 0 ? ` + ${usd(botPowerMiningUsd, 2)} hashrate mining` : ""}${(serverSalesUsd + botPowerReferralUsd) > 0 ? ` + ${usd(serverSalesUsd + botPowerReferralUsd, 2)} referral & sales` : ""}${botStakeLevelUsd > 0 ? ` + ${usd(botStakeLevelUsd, 2)} staking level income` : ""}`
                    : `${Number(botTotalBot).toLocaleString(undefined, { maximumFractionDigits: 4 })} BOT earned · BOT price unavailable`}
                  hint="View" color="#15DCAC" icon={Server} active={view === "botmining"} onClick={() => setView(view === "botmining" ? "all" : "botmining")} tid="dash-kpi-botmining"
                  onBreakdown={() => setBreakdown(breakdown === "bot_community" ? null : "bot_community")}
                  breakdownOpen={breakdown === "bot_community"} />
                <StreamKPI label="Staking" value={usd(totalStakingUsd)} sub={botStakeUsd > 0
                  ? `${totalStakingCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA + ${botStakeBot.toLocaleString(undefined, { maximumFractionDigits: 2 })} BOT earned`
                  : `${totalStakingCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA earned × $${caPrice.toFixed(2)}/CA`} hint={`${positions.length + (botStake?.totals?.positions || 0)} positions · rewards only`} color="#C2185B" icon={Coins} active={view === "staking"} onClick={() => setView(view === "staking" ? "all" : "staking")} tid="dash-kpi-staking"
                  onBreakdown={() => setBreakdown(breakdown === "staking" ? null : "staking")}
                  breakdownOpen={breakdown === "staking"} />
                {showCommunity && <StreamKPI label="Team rewards" value={usd(caCommFamilyUsd)} sub={vipReferralUsd > 0 ? `${usd(totalCommunityUsd)} community + ${usd(vipReferralUsd, 2)} VIP hashrate referral` : "paid to you by your team"} hint="View" color="#7C3AED" icon={Users} active={view === "community"} onClick={() => setView(view === "community" ? "all" : "community")} tid="dash-kpi-community"
                  onBreakdown={() => setBreakdown(breakdown === "ca_community" ? null : "ca_community")}
                  breakdownOpen={breakdown === "ca_community"} />}
                <StreamKPI label="Total income" value={usd(grandTotal)} sub={showCommunity ? `${usd(totalMiningUsd)} + ${usd(totalStakingUsd)} + ${usd(caCommFamilyUsd)} + ${usd(botFamilyUsd)}` : `${usd(totalMiningUsd)} + ${usd(totalStakingUsd)} + ${usd(botFamilyUsd)}`} hint="Overview" icon={TrendingUp} gradient active={view === "all"} onClick={() => setView("all")} tid="dash-kpi-total" />
              </div>
              <LiquidValueCard portfolioId={effSel} />
              {breakdown && (
                <StreamBreakdown
                  portfolioId={effSel}
                  parentKey={breakdown}
                  tileLabel={breakdown === "ca_community" ? "Team rewards" : breakdown === "staking" ? "Staking" : "BOT earnings"}
                  tileUsd={breakdown === "ca_community" ? caCommFamilyUsd : breakdown === "staking" ? totalStakingUsd : botFamilyUsd}
                  onClose={() => setBreakdown(null)}
                />
              )}
              <div className="min-w-0">
                {view === "mining" && <MiningView entries={mining} wallets={ledger.wallets} />}
                {view === "botmining" && <BotMiningView totals={hold?.totals} wallets={hold?.wallets} botPrice={botPrice} daily={hold?.series} rewardSeries={hold?.reward_series} />}
                {view === "staking" && <StakingView positions={positions} stakingEntries={stakingEntries} botStake={botStake} caPrice={caPrice} botPrice={botPrice} wallets={ledger.wallets} holdTotals={hold?.totals} snapshotSeries={hold?.series} />}
                {view === "community" && showCommunity && <CommunityView rows={communityData} rewardSeries={hold?.reward_series} />}
                {view === "all" && (
                  <div className="glass p-5 h-full" data-testid="dash-combined-chart">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(64,96,255,0.12)" }}><Layers size={16} style={{ color: "#4060FF" }} /></div>
                      <div>
                        <h3 className="font-display text-lg font-semibold tracking-tight leading-tight">Combined revenue over time</h3>
                        <div className="kpi-label flex items-center gap-1.5 mt-0.5"><Sparkles size={10} /> each stream at its own value · combined total dotted · zoomable brush</div>
                      </div>
                      <div className="ml-auto flex items-center gap-2 flex-wrap">
                        <PillToggle
                          value={rangeKey}
                          onChange={setRangeKey}
                          options={[{ value: "7d", label: "7d" }, { value: "30d", label: "30d" }, { value: "90d", label: "90d" }, { value: "all", label: "All" }]}
                          testid="range-toggle"
                        />
                        <PillToggle
                          value={chartMode}
                          onChange={setChartMode}
                          options={[{ value: "daily", label: "Daily" }, { value: "cumulative", label: "Cumulative" }]}
                          testid="mode-toggle"
                        />
                      </div>
                    </div>

                    {seriesView.length > 0 && (
                      <div className="flex flex-wrap items-stretch gap-2 mb-3 text-xs">
                        {(() => {
                          const last = seriesView[seriesView.length - 1];
                          const lastTotal = chartMode === "cumulative" ? last.dailyTotal : ((last.mining || 0) + (last.staking || 0) + (last.community || 0) + (last.bot || 0));
                          return (
                            <div className="kpi-tile !p-2.5 flex items-center gap-3" data-testid="dash-chart-latest">
                              <div><div className="kpi-label leading-tight">Latest · {last.date}</div><div className="num font-semibold" style={{ fontSize: 15, lineHeight: 1.1 }}>{usd(lastTotal)}<span className="text-ink-3 font-normal text-[10px]">/d</span></div></div>
                              <div className="text-ink-3 text-xs">×30</div>
                              <div><div className="kpi-label leading-tight">30-day pace</div><div className="num font-semibold" style={{ fontSize: 15, lineHeight: 1.1, color: "#00863A" }}>{usd(lastTotal * 30)}</div></div>
                            </div>
                          );
                        })()}
                        {chartMode === "daily" && dailyAvg != null && (
                          <div className="kpi-tile !p-2.5 flex items-center gap-3" data-testid="dash-chart-avg">
                            <div><div className="kpi-label leading-tight">Window avg</div><div className="num font-semibold" style={{ fontSize: 15, lineHeight: 1.1 }}>{usd(dailyAvg)}<span className="text-ink-3 font-normal text-[10px]">/d</span></div></div>
                            <div className="text-ink-3 text-xs">×30</div>
                            <div><div className="kpi-label leading-tight">Trend pace</div><div className="num font-semibold" style={{ fontSize: 15, lineHeight: 1.1, color: "#1D4ED8" }}>{usd(dailyAvg * 30)}</div></div>
                          </div>
                        )}
                        {peakDay && chartMode === "daily" && (
                          <div className="kpi-tile !p-2.5 flex items-center gap-2" data-testid="dash-chart-peak">
                            <Trophy size={14} style={{ color: "#B45309" }} />
                            <div><div className="kpi-label leading-tight">Peak · {peakDay.date}</div><div className="num font-semibold" style={{ fontSize: 15, lineHeight: 1.1, color: "#B45309" }}>{usd(peakDay.dailyTotal)}</div></div>
                          </div>
                        )}
                      </div>
                    )}

                    {seriesView.length === 0 ? (
                      <div className="text-sm text-ink-3 py-12 text-center" data-testid="dash-combined-empty">No revenue in this window — pick a wider range, or pull the latest from Wallets → Sync settings.</div>
                    ) : (
                      <div style={{ height: 340 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={seriesView} margin={{ top: 36, right: 42, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="gMining" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8D1CEB" stopOpacity={0.55} /><stop offset="100%" stopColor="#8D1CEB" stopOpacity={0.04} /></linearGradient>
                              <linearGradient id="gBot" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#15DCAC" stopOpacity={0.55} /><stop offset="100%" stopColor="#15DCAC" stopOpacity={0.04} /></linearGradient>
                              <linearGradient id="gStaking" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C2185B" stopOpacity={0.55} /><stop offset="100%" stopColor="#C2185B" stopOpacity={0.04} /></linearGradient>
                              <linearGradient id="gCommunity" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7C3AED" stopOpacity={0.55} /><stop offset="100%" stopColor="#7C3AED" stopOpacity={0.04} /></linearGradient>
                            </defs>
                            <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                            <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} />
                            <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                            <Tooltip content={<DailyTooltip showCommunity={showCommunity} />} cursor={{ stroke: "rgba(141,28,235,0.18)", strokeWidth: 1 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                            {/* Not stacked — every stream is drawn at its OWN value so the
                                bigger earner always sits higher on the chart. The dotted
                                blue line carries the combined total. */}
                            {showCommunity && <Area type="monotone" dataKey="community" name="Community" stroke="#7C3AED" strokeWidth={2} fill="url(#gCommunity)" fillOpacity={0.5} isAnimationActive animationDuration={700} />}
                            <Area type="monotone" dataKey="bot" name="BOT Mining" stroke="#15DCAC" strokeWidth={2} fill="url(#gBot)" fillOpacity={0.5} isAnimationActive animationDuration={700} />
                            <Area type="monotone" dataKey="staking" name="Staking" stroke="#C2185B" strokeWidth={2} fill="url(#gStaking)" fillOpacity={0.5} isAnimationActive animationDuration={700} />
                            <Area type="monotone" dataKey="mining" name="CA Mining" stroke="#8D1CEB" strokeWidth={2} fill="url(#gMining)" fillOpacity={0.5} isAnimationActive animationDuration={700} />
                            <Line type="monotone" dataKey="allStreams" name="All streams" stroke="#0B1020" strokeWidth={3} dot={false} activeDot={{ r: 5, fill: "#0B1020", stroke: "#fff", strokeWidth: 2 }} isAnimationActive animationDuration={800} />
                            {chartMode === "daily" && (
                              <Line type="monotone" dataKey="rolling7" name="7-day avg" stroke="#00863A" strokeWidth={2.25} strokeDasharray="6 3" dot={false} activeDot={{ r: 4, fill: "#00863A" }} isAnimationActive animationDuration={900} />
                            )}
                            {chartMode === "daily" && dailyAvg != null && dailyAvg > 0 && (
                              <ReferenceLine y={dailyAvg} stroke="#1D4ED8" strokeDasharray="3 3" strokeOpacity={0.4} label={{ value: `Avg $${dailyAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, position: "insideTopRight", fill: "#1D4ED8", fontSize: 10, fontWeight: 600 }} />
                            )}
                            {chartMode === "daily" && peakDay && peakDay.dailyTotal > 0 && (
                              <ReferenceDot x={peakDay.date} y={peakDay.dailyTotal} r={6} fill="#B45309" stroke="#fff" strokeWidth={2} ifOverflow="extendDomain" label={{ value: `Peak $${peakDay.dailyTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, position: "top", fill: "#B45309", fontSize: 10, fontWeight: 700 }} />
                            )}
                            {seriesView.length > 14 && (
                              <Brush dataKey="date" height={18} stroke="rgba(64,96,255,0.45)" travellerWidth={8} fill="rgba(64,96,255,0.04)" tickFormatter={() => ""} />
                            )}
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* One quiet wallet used to cut this chart short. It no longer does —
                        so say which one went quiet, or the flat tail reads as "everything
                        stopped". One line, same weight as a footnote, no alarm. */}
                    {behindWallets.length > 0 && seriesView.length > 0 && (
                      <div className="text-[11px] text-ink-3 mt-2.5" data-testid="dash-behind-note">
                        {behindWallets.length === 1 ? (
                          <><strong className="font-semibold">{behindWallets[0].name}</strong> stopped reporting {fmtShortDay(behindWallets[0].last)}.</>
                        ) : (
                          <>
                            {behindWallets.slice(0, 3).map((w, i) => (
                              <span key={w.id}>
                                {i > 0 ? ", " : ""}
                                <strong className="font-semibold">{w.name}</strong> ({fmtShortDay(w.last)})
                              </span>
                            ))}
                            {behindWallets.length > 3 && ` and ${behindWallets.length - 3} other wallet${behindWallets.length - 3 === 1 ? "" : "s"}`}
                            {" stopped reporting."}
                          </>
                        )}
                        {" Their earnings so far still count — the lines simply stop climbing for them."}
                        {isAdmin && (
                          <>
                            {" "}
                            <Link to={`/admin/not-reporting?q=${encodeURIComponent(behindWallets[0].address || behindWallets[0].name)}`}
                              className="underline hover:no-underline" data-testid="dash-behind-link">See the full list</Link>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {view === "all" && (
              <>
                {/* Every CaryPact figure for every wallet, with totals for a selection */}
                <div className="mb-4">
                  <WalletLedger wallets={hold?.wallets || []} caPrice={caPrice} />
                </div>

                {/* Mining & staking detail — own scale so the small streams are visible */}
                <div className="glass p-5 mb-4" data-testid="dash-small-streams-chart">
                  <div className="mb-3">
                    <h3 className="font-display text-lg font-semibold tracking-tight">Mining & staking over time</h3>
                    <p className="text-xs text-ink-3">Same period and mode as the chart above — separate scale so the smaller streams don't get lost under team rewards</p>
                  </div>
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={seriesView} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,18,38,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} minTickGap={28} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} width={56} />
                        <Tooltip formatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} labelStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="mining" name="CA Mining" stroke="#8D1CEB" fill="rgba(141,28,235,0.14)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="bot" name="BOT Mining" stroke="#15DCAC" fill="rgba(21,220,172,0.14)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="staking" name="Staking" stroke="#C2185B" fill="rgba(194,24,91,0.12)" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex gap-4 justify-center mt-1 text-[11px] text-ink-2">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: "#8D1CEB" }} /> CA Mining</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: "#15DCAC" }} /> BOT Mining</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: "#C2185B" }} /> Staking</span>
                  </div>
                </div>

                {/* Revenue mix */}
                <div className="glass p-3 mb-4" data-testid="dash-mix">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="kpi-label">Revenue mix</div>
                    {grandTotal > 0 && (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                        {STREAMS.filter((s) => showCommunity || s.key !== "community").map((s) => {
                          const pct = grandTotal > 0 ? (totals[s.key] / grandTotal) * 100 : 0;
                          return (
                            <div key={s.key} className="flex items-center gap-1.5 text-xs">
                              <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color }} />
                              <span className="text-ink-2">{s.label}</span>
                              <span className="num font-semibold">{pct.toFixed(1)}%</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {grandTotal > 0 ? (
                    <div className="flex w-full h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.05)" }}>
                      {STREAMS.filter((s) => showCommunity || s.key !== "community").map((s) => {
                        const pct = grandTotal > 0 ? (totals[s.key] / grandTotal) * 100 : 0;
                        return pct > 0 ? <div key={s.key} style={{ width: `${pct}%`, background: s.color }} title={`${s.label} ${pct.toFixed(1)}%`} /> : null;
                      })}
                    </div>
                  ) : <div className="text-sm text-ink-3 py-1">No revenue imported yet — pull it from CaryPact via Wallets → Sync settings.</div>}
                </div>

                {/* Revenue by period — full width */}
                <div className="glass p-5 mb-4" data-testid="dash-period-bars">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(124,58,237,0.12)" }}><Layers size={16} style={{ color: "#7C3AED" }} /></div>
                      <div>
                        <h3 className="font-display text-lg font-semibold tracking-tight leading-tight">Revenue by period</h3>
                        <div className="kpi-label flex items-center gap-1.5 mt-0.5"><Sparkles size={10} /> {barMode === "total" ? "one bar per period · period delta · best-period flag" : "grouped bars · period delta · best-period flag"}</div>
                      </div>
                      <div className="ml-auto flex items-center gap-2 flex-wrap">
                        <PillToggle
                          value={barMode}
                          onChange={setBarMode}
                          options={[{ value: "total", label: "By total" }, { value: "category", label: "By category" }]}
                          testid="bar-mode"
                        />
                        <PillToggle
                          value={barGrain}
                          onChange={setBarGrain}
                          options={[{ value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }]}
                          testid="bar-grain"
                        />
                        {barAvg != null && (
                          <div className="kpi-tile !p-2.5 flex items-center gap-2" data-testid="dash-period-avg" title="The dashed line across the chart">
                            <span aria-hidden className="w-4 border-t-2 border-dashed" style={{ borderColor: "#1D4ED8", opacity: 0.6 }} />
                            <div><div className="kpi-label leading-tight">Avg / {{ daily: "day", weekly: "week", monthly: "month" }[barGrain] || "period"}</div><div className="num font-semibold" style={{ fontSize: 15, lineHeight: 1.1 }}>{usd(barAvg)}</div></div>
                          </div>
                        )}
                      </div>
                    </div>
                    {barView.length === 0 ? (
                      <div className="text-sm text-ink-3 py-14 text-center" data-testid="dash-period-empty">No revenue history yet — import it from CaryPact via Wallets → Sync settings.</div>
                    ) : (
                      <div style={{ height: barGrain === "daily" ? 280 : 336 }} data-testid="dash-period-bars-canvas">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={barView} margin={{ top: 36, right: 16, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="bMining" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#A855F7" /><stop offset="100%" stopColor="#7C10D6" /></linearGradient>
                              <linearGradient id="bBot" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3BE9C0" /><stop offset="100%" stopColor="#0FB894" /></linearGradient>
                              <linearGradient id="bStaking" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E63479" /><stop offset="100%" stopColor="#A8164E" /></linearGradient>
                              <linearGradient id="bCommunity" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9263F2" /><stop offset="100%" stopColor="#6A2BD8" /></linearGradient>
                              <linearGradient id="bTotal" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4060FF" /><stop offset="100%" stopColor="#1B2FB8" /></linearGradient>
                            </defs>
                            <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                            <XAxis dataKey="label" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false}
                              interval={barGrain === "daily" ? undefined : 0}
                              minTickGap={barGrain === "daily" ? 18 : 0}
                              height={barGrain === "daily" ? 24 : 52}
                              tick={(props) => <PeriodTick {...props} rows={barView} withTotal={barGrain !== "daily"} dense={barDense} />} />
                            <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                            <Tooltip content={<PeriodTooltip showCommunity={showCommunity} />} cursor={{ fill: "rgba(141,28,235,0.05)" }} />
                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: barGrain === "daily" ? 0 : 10 }} iconSize={8} />
                            {/* By total by default: one bar per period, so a dip in one
                                stream can't read as the whole business shrinking. */}
                            {barMode === "total" ? (
                              <Bar dataKey="total" name="Total revenue" fill="url(#bTotal)" radius={[5, 5, 0, 0]} maxBarSize={barGrain === "daily" ? 16 : 38} isAnimationActive animationDuration={700} />
                            ) : (
                              <>
                                <Bar dataKey="mining" name="CA Mining" fill="url(#bMining)" radius={[5, 5, 0, 0]} maxBarSize={barGrain === "daily" ? 14 : 30} isAnimationActive animationDuration={700} />
                                <Bar dataKey="bot" name="BOT Mining" fill="url(#bBot)" radius={[5, 5, 0, 0]} maxBarSize={barGrain === "daily" ? 14 : 30} isAnimationActive animationDuration={700} />
                                <Bar dataKey="staking" name="Staking" fill="url(#bStaking)" radius={[5, 5, 0, 0]} maxBarSize={barGrain === "daily" ? 14 : 30} isAnimationActive animationDuration={700} />
                                {showCommunity && (
                                  <Bar dataKey="community" name="Community" fill="url(#bCommunity)" radius={[5, 5, 0, 0]} maxBarSize={barGrain === "daily" ? 14 : 30} isAnimationActive animationDuration={700} />
                                )}
                              </>
                            )}
                            {barAvg != null && barAvg > 0 && (
                              /* The line only, no caption — it used to print "Avg $9,049" inside the
                                 plot, where the last bar sat on top of it. The number lives in the
                                 Avg / period tile above the chart instead. */
                              <ReferenceLine y={barAvg} stroke="#1D4ED8" strokeDasharray="3 3" strokeOpacity={0.4} />
                            )}
                            {barGrain === "daily" && barView.length > 30 && (
                              <Brush dataKey="label" height={18} stroke="rgba(124,58,237,0.45)" travellerWidth={8} fill="rgba(124,58,237,0.04)" tickFormatter={() => ""} />
                            )}
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                {/* Top wallets + daily breakdown (follows the portfolio picked above) */}
                {!notLinked && (
                  <>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div className="glass-strong p-6" data-testid="dash-top-wallets">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                            <Trophy size={15} style={{ color: "#B45309" }} /> Top wallets
                          </h3>
                          <Link to="/wallets" className="text-xs font-semibold hover:underline" style={{ color: "#4060FF" }} data-testid="dash-see-all-wallets">See all {t.wallets ?? 0} wallets →</Link>
                        </div>
                        {topWallets.length === 0 ? (
                          <div className="text-sm text-ink-3 font-mono py-8 text-center" data-testid="dash-top-wallets-empty">No synced wallets in this view yet.</div>
                        ) : (
                          <>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-white z-10">
                                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-3">
                                  <th className="py-2 pr-3">#</th>
                                  <th className="py-2 pr-3 cursor-pointer select-none hover:text-ink" data-testid="top-sort-name" onClick={() => toggleTopSort("name")}>Person{sortArrow("name")}</th>
                                  <th className="py-2 pr-3 cursor-pointer select-none hover:text-ink" data-testid="top-sort-address" onClick={() => toggleTopSort("address")}>Wallet{sortArrow("address")}</th>
                                  <th className="py-2 pr-3 cursor-pointer select-none hover:text-ink" data-testid="top-sort-rank" onClick={() => toggleTopSort("rank")}>Rank{sortArrow("rank")}</th>
                                  <th className="py-2 pr-3 text-right cursor-pointer select-none hover:text-ink" data-testid="top-sort-mining" onClick={() => toggleTopSort("mining")}>Mining locked ${sortArrow("mining")}</th>
                                  <th className="py-2 pr-3 text-right cursor-pointer select-none hover:text-ink" title="CaryPact team effective performance under this wallet — the same Downline $ the org tile shows" data-testid="top-sort-downline" onClick={() => toggleTopSort("downline")}>Downline ${sortArrow("downline")}</th>
                                  <th className="py-2 text-right cursor-pointer select-none hover:text-ink" title="CA staked at the CA price plus CaryPact's BOT pledge at the BOT price — the same two assets the staking tile adds up" data-testid="top-sort-staked" onClick={() => toggleTopSort("staked")}>Staked ${sortArrow("staked")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {topPager.pageItems.map((w, i) => (
                                  <tr key={w.node_id} className="border-t" style={{ borderColor: "rgba(15,18,38,0.05)" }} data-testid={`dash-top-wallet-${topPager.from + i - 1}`}>
                                    <td className="py-2 pr-3 font-mono text-xs text-ink-3">{topPager.from + i}</td>
                                    <td className="py-2 pr-3 font-semibold text-ink">{w.name || "—"}</td>
                                    <td className="py-2 pr-3">
                                      <button onClick={() => copyAddr(w.address)} className="inline-flex items-center gap-1.5 font-mono text-[12px] text-ink-2 hover:text-ink" title={`${w.address} — click to copy`}>
                                        {shortAddr(w.address)} <Copy size={10} className="text-ink-3" />
                                      </button>
                                    </td>
                                    <td className="py-2 pr-3">
                                      {w.rank ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(217,119,6,0.12)", color: "#B45309" }}>{w.rank}</span> : <span className="text-ink-3 text-[12px]">—</span>}
                                    </td>
                                    <td className="py-2 pr-3 text-right font-mono text-xs tabular-nums">{usd(w.mining_usdt)}</td>
                                    <td className="py-2 pr-3 text-right font-mono text-xs tabular-nums" data-testid={`dash-top-downline-${topPager.from + i - 1}`}>
                                      {w.team_lock_usd > 0 ? usd(w.team_lock_usd) : <span className="text-ink-3">—</span>}
                                    </td>
                                    <td className="py-2 text-right font-mono text-xs tabular-nums font-semibold"
                                      title={(w.staked_bot || 0) > 0
                                        ? `${Number(w.staked_ca || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} CA staked + ${Number(w.staked_bot).toLocaleString(undefined, { maximumFractionDigits: 4 })} BOT pledged`
                                        : `${Number(w.staked_ca || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} CA staked`}>
                                      {usd(w.staked_usd)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <TablePager pager={topPager} testId="dash-top-wallets-pager" label="wallets" />
                          </>
                        )}
                      </div>

                      <div className="glass-strong p-6" data-testid="dash-daily-table">
                        <h3 className="font-display text-lg font-semibold tracking-tight mb-3 flex items-center gap-2">
                          <Table2 size={15} style={{ color: "#7C3AED" }} /> Daily breakdown
                        </h3>
                        {series.length === 0 ? (
                          <div className="text-sm text-ink-3 font-mono py-8 text-center" data-testid="dash-daily-empty">Daily rows appear once revenue is imported.</div>
                        ) : (
                          <>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-white z-10">
                                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-3">
                                  <th className="py-2 pr-3">Date</th>
                                  <th className="py-2 pr-3 text-right">Mining $</th>
                                  <th className="py-2 pr-3 text-right">BOT $</th>
                                  <th className="py-2 pr-3 text-right">Staking $</th>
                                  {showCommunity && <th className="py-2 pr-3 text-right">Team $</th>}
                                  <th className="py-2 text-right">Total $</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dailyPager.pageItems.map((r) => (
                                  <tr key={r.date} className="border-t" style={{ borderColor: "rgba(15,18,38,0.05)" }} data-testid={`dash-daily-row-${r.date}`}>
                                    <td className="py-1.5 pr-3 font-mono text-xs">{r.date}</td>
                                    <td className="py-1.5 pr-3 text-right font-mono text-xs tabular-nums">{usd(r.mining, 2)}</td>
                                    <td className="py-1.5 pr-3 text-right font-mono text-xs tabular-nums">{usd(r.bot || 0, 2)}</td>
                                    <td className="py-1.5 pr-3 text-right font-mono text-xs tabular-nums">{usd(r.staking, 2)}</td>
                                    {showCommunity && <td className="py-1.5 pr-3 text-right font-mono text-xs tabular-nums">{usd(r.community, 2)}</td>}
                                    <td className="py-1.5 text-right font-mono text-xs tabular-nums font-semibold">{usd(r.mining + r.staking + r.community + (r.bot || 0), 2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <TablePager pager={dailyPager} testId="dash-daily-pager" label="days" />
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            <div className="text-xs text-ink-3 mt-4">Mining & community are actual imported CaryPact rewards; staking daily yield is estimated from position rate × principal. CA price used: ${caPrice.toFixed(2)}. Returns are not guaranteed.</div>
          </>
        )}
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-8 mb-4">
        <WhatIsBotChainCP />
      </div>

      {modal !== null && (
        <PortfolioModal
          portfolio={modal.id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={async (p) => { setModal(null); await loadPortfolios(); pick(p.id); setRefreshKey((k) => k + 1); }}
          onDeleted={async () => { setModal(null); await loadPortfolios(); pick("downline"); setRefreshKey((k) => k + 1); }}
        />
      )}
      {prodPullOpen && <ProdPullModal onClose={() => setProdPullOpen(false)} />}
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
      <HelpFab />
    </div>
  );
}
